/**
 * Resolve variant_id for a given size from variant_ids map or product list.
 * Handles your API format: size is "S ¬∑ 38", "M ¬∑ 40", "2XL ¬∑ 46", etc.
 */

const SIZE_ALIASES: Record<string, string[]> = {
  S: ['S', 's', 'Small', 'SMALL', 'small'],
  M: ['M', 'm', 'Medium', 'MEDIUM', 'medium'],
  L: ['L', 'l', 'Large', 'LARGE', 'large'],
  XL: ['XL', 'xl', 'Xl', 'Extra Large', 'Extra large', 'EXTRALARGE'],
  XXL: ['XXL', 'xxl', 'Xxl', '2XL', '2xl'],
  XXXL: ['XXXL', 'xxxl', 'Xxxl', '3XL', '3xl'],
};

/**
 * Your API returns size like "S ¬∑ 38", "M ¬∑ 40", "2XL ¬∑ 46".
 * Extract the size code (first token: S, M, 2XL, etc.).
 */
export function parseSizeFromApi(apiSize: string | undefined): string {
  if (!apiSize || typeof apiSize !== 'string') return '';
  const trimmed = apiSize.trim();
  const firstToken = trimmed.split(/\s+/)[0];
  return firstToken ? firstToken.trim() : trimmed;
}

function normalizeSize(size: string | undefined): string {
  if (!size || typeof size !== 'string') return '';
  const s = size.trim().toUpperCase();
  if (s === 'S' || s === 'SMALL') return 'S';
  if (s === 'M' || s === 'MEDIUM') return 'M';
  if (s === 'L' || s === 'LARGE') return 'L';
  if (s === 'XL' || s === 'EXTRA LARGE' || s === 'EXTRALARGE') return 'XL';
  if (s === 'XXL' || s === '2XL') return 'XXL';
  if (s === 'XXXL' || s === '3XL') return 'XXXL';
  return s;
}

export interface ProductVariant {
  size: string;
  variant_id: string;
}

/**
 * Convert a `variants` array (e.g. [{ size: "S - 38", variant_id: "123" }])
 * into a `variant_ids` map keyed by the parsed size code (e.g. { S: "123" }).
 */
export function variantsToMap(variants: ProductVariant[] | undefined): Record<string, string> {
  const map: Record<string, string> = {};
  if (!Array.isArray(variants)) return map;
  for (const v of variants) {
    if (!v?.size || !v?.variant_id) continue;
    const key = parseSizeFromApi(v.size) || v.size;
    if (!(key in map)) map[key] = v.variant_id;
  }
  return map;
}

/**
 * Look up variant_id from variant_ids map using the selected size.
 * Tries exact key, normalized key, and all known aliases (Small, Medium, s, m, etc.).
 */
export function getVariantIdFromMap(
  variantIds: Record<string, string> | undefined,
  selectedSize: string
): string | undefined {
  if (!variantIds || typeof variantIds !== 'object') return undefined;
  const normalized = normalizeSize(selectedSize);
  if (variantIds[selectedSize]) return variantIds[selectedSize];
  if (variantIds[normalized]) return variantIds[normalized];
  const aliases = SIZE_ALIASES[normalized];
  if (aliases) {
    for (const key of aliases) {
      if (variantIds[key] !== undefined) return variantIds[key];
    }
  }
  const keys = Object.keys(variantIds);
  for (const key of keys) {
    if (normalizeSize(key) === normalized) return variantIds[key];
  }
  return undefined;
}

export { normalizeSize };
